import {useEffect, useState, type FormEvent} from 'react';
import {ErrorNotice, Loading} from './components';
import {errorDetails, type hermesActions} from './lib/store';
import type {ApplicationRegistration, ApplicationStatus, ErrorDetails, HermesState} from './lib/types';
import './application-settings.css';

type Props = {state: HermesState; actions: typeof hermesActions};
type Draft = {kind: ApplicationRegistration['kind']; slug: string; directory: string; command: string; cwd: string; port: string; healthPath: string};
const emptyDraft = (): Draft => ({kind: 'static', slug: '', directory: '', command: '', cwd: '', port: '', healthPath: ''});

function ServiceRecord({application, busy, actions}: {application: ApplicationStatus; busy: boolean; actions: typeof hermesActions}) {
  const definition = application.definition;
  return <article className="application-record" aria-label={`Service ${application.slug}`}>
    <div className="application-record-heading"><h3>{application.slug}</h3><p>{application.state}</p></div>
    <p className="field-help">{definition.kind === 'static' ? 'Static files' : definition.kind === 'http' ? 'Web service' : 'Background worker'}</p>
    <p className="field-help application-definition">{definition.kind === 'static' ? definition.directory : definition.cwd}</p>
    {application.path !== null && application.applicationUrl !== null && <p className="field-help application-address">Assigned address: {application.state === 'running'
      ? <a href={application.applicationUrl} target="_blank" rel="noopener noreferrer">{application.applicationUrl}</a>
      : <span>{application.applicationUrl}</span>}</p>}
    {application.path !== null && application.state === 'running' && <p className="field-help">Public access has not been checked.</p>}
    {application.exitCode !== null && <p className="field-help">Exit code: {application.exitCode}</p>}
    {application.error !== null && <ErrorNotice title="There is a problem with this application" error={{name:'ApplicationError',message:application.error}}/>}
    <div className="button-row">
      <button className="button" type="button" disabled={busy || application.state === 'running' || application.state === 'starting'}
        onClick={() => void actions.startApplication(application.slug).catch(() => undefined)}>Start</button>
      <button className="text-button" type="button" disabled={busy || (!application.desiredRunning && application.state !== 'running' && application.state !== 'starting')}
        onClick={() => void actions.stopApplication(application.slug).catch(() => undefined)}>Stop</button>
      <button className="text-button" type="button" disabled={busy}
        onClick={() => void actions.readApplicationLogs(application.slug).catch(() => undefined)}>Logs</button>
    </div>
  </article>;
}

export function ApplicationSettings({state, actions}: Props) {
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [adding, setAdding] = useState(false);
  const [formError, setFormError] = useState<ErrorDetails | null>(null);
  const accountId = state.session.data?.accountId;
  const resource = state.accountApplications;
  const busy = state.operation.label !== null;
  const logs = state.applicationLogs;

  useEffect(() => {
    if (accountId) void actions.refreshApplications().catch(() => undefined);
  }, [accountId, actions]);

  function changeDraft(patch: Partial<Draft>): void {
    setDraft(previous => ({...previous, ...patch}));
    setFormError(null);
  }

  async function register(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy) return;
    setFormError(null);
    let definition: ApplicationRegistration;
    try {
      if (draft.kind === 'static') definition = {slug: draft.slug, kind: 'static', directory: draft.directory};
      else {
        const command: unknown = JSON.parse(draft.command);
        if (!Array.isArray(command) || command.length === 0 || !command.every(item => typeof item === 'string') || !command[0]?.trim()) {
          throw new TypeError('Command must be a non-empty JSON array of text values, beginning with the program to run.');
        }
        definition = draft.kind === 'http'
          ? {slug: draft.slug, kind: 'http', command, cwd: draft.cwd, port: Number(draft.port), healthPath: draft.healthPath}
          : {slug: draft.slug, kind: 'worker', command, cwd: draft.cwd};
      }
    } catch (error) {setFormError(errorDetails(error)); return;}
    try {
      await actions.registerApplication(definition);
      setDraft(emptyDraft());
      setAdding(false);
    } catch { /* Keep the draft. The shared operation notice displays the complete error. */ }
  }

  return <section className="settings-section application-settings" aria-labelledby="application-settings-title">
    <div className="settings-description"><h2 id="application-settings-title">Applications</h2>
      <p>Serve project files, run web services, or start background work.</p>
      <p>Web services and static files use your assigned application address. Background workers have no public address.</p>
      <p>Each service uses files in your private workspace.</p>
      {resource.data && <p className="field-help">{resource.data.policy.description}</p>}
    </div><div className="application-controls">
      {!adding && <div className="button-row"><button type="button" className="button" disabled={busy}
        onClick={() => setAdding(true)}>Add service</button></div>}
      <ErrorNotice error={resource.error}/>
      <Loading active={resource.status === 'idle' || resource.status === 'loading'}/>
      {resource.data && <div className="application-list">
        {resource.data.applications.length === 0 && <p className="field-help">You have no registered services.</p>}
        {resource.data.applications.map(application => <ServiceRecord key={application.slug} application={application} busy={busy} actions={actions}/>)}</div>}
      {state.applicationLogSlug !== null && <section className="application-logs" aria-label={`Logs for ${state.applicationLogSlug}`}>
        <div className="application-record-heading"><h3>Logs: {state.applicationLogSlug}</h3>
          <button type="button" className="text-button" onClick={actions.closeApplicationLogs}>Close logs</button></div>
        <ErrorNotice error={logs.error}/>
        {logs.status === 'loading' && <Loading/>}
        {logs.data && (logs.data.available ? <pre tabIndex={0} aria-label={`Service log output for ${state.applicationLogSlug}`}>{logs.data.text || 'The log is empty.'}</pre> : <p className="field-help">No log file is available.</p>)}
      </section>}
      {adding && <form className="application-form" aria-busy={busy} onSubmit={event => void register(event)}>
        <h3>Add service</h3>
        <ErrorNotice error={formError}/>
        <label className="field"><span>Service type</span><select value={draft.kind} disabled={busy} autoFocus
          onChange={event => changeDraft({kind: event.target.value as Draft['kind']})}>
          <option value="static">Static files</option><option value="http">Web service</option><option value="worker">Background worker</option>
        </select></label>
        <label className="field"><span>Service name</span><input value={draft.slug} disabled={busy} required autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false}
          onChange={event => changeDraft({slug: event.target.value})}/></label>
        {draft.kind === 'static' ? <label className="field"><span>Files directory</span><input value={draft.directory} disabled={busy} required autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false}
          onChange={event => changeDraft({directory: event.target.value})}/></label> : <>
          <label className="field"><span>Working directory</span><input value={draft.cwd} disabled={busy} required autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false}
            onChange={event => changeDraft({cwd: event.target.value})}/></label>
          <label className="field"><span>Command arguments</span><textarea rows={3} value={draft.command} disabled={busy} required autoCapitalize="none" autoCorrect="off" spellCheck={false}
            aria-describedby="application-command-help" onChange={event => changeDraft({command: event.target.value})}/></label>
          <p className="field-help" id="application-command-help">Enter a JSON array. The first value is the program. Each next value is an argument.</p>
          {draft.kind === 'http' && <>
            <label className="field"><span>Internal port</span><input type="number" inputMode="numeric" min={1024} max={65535} step={1} value={draft.port} disabled={busy} required
              onChange={event => changeDraft({port: event.target.value})}/></label>
            <label className="field"><span>Health check path</span><input value={draft.healthPath} disabled={busy} required autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false}
              onChange={event => changeDraft({healthPath: event.target.value})}/></label>
          </>}
        </>}
        <div className="button-row"><button type="submit" className="button" disabled={busy}>Save service</button>
          <button type="button" className="text-button" disabled={busy} onClick={() => {setAdding(false); setFormError(null);}}>Cancel</button></div>
      </form>}
    </div>
  </section>;
}
